import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { jsPDF } from 'jspdf';
import logo from '@/assets/logo.png';

const Receipt = ({ transaction, isOpen, onClose }) => {
    const getInvoiceId = (id) => {
        return `INV-${id.substring(0, 8).toUpperCase()}`;
    };

    const handleDownload = async () => {
        return new Promise((resolve) => {
            const doc = new jsPDF();
            const invoiceId = getInvoiceId(transaction.id);
            const dateTime = format(new Date(transaction.created_at), 'MMMM dd, yyyy - HH:mm');

            // Create an image element to get dimensions
            const img = new Image();
            img.src = logo;

            // Set font sizes
            const subtitleSize = 12;
            const normalSize = 12;
            const smallSize = 10;

            // Calculate positions
            let yPos = 20;
            const leftMargin = 20;
            const rightMargin = 190;
            const lineHeight = 8;

            img.onload = () => {
                // Calculate image dimensions to maintain aspect ratio
                const aspectRatio = img.width / img.height;
                const logoWidth = 40;
                const logoHeight = logoWidth / aspectRatio;

                // Center the logo horizontally
                const logoX = (doc.internal.pageSize.width - logoWidth) / 2;

                // Add logo
                doc.addImage(logo, 'PNG', logoX, yPos - 1, logoWidth, logoHeight);

                // Adjust yPos to account for logo height
                yPos += logoHeight + lineHeight;

                // Subtitle
                doc.setFontSize(subtitleSize);
                doc.text('Credit Purchase Receipt', 105, yPos, { align: 'center' });
                yPos += lineHeight * 3;

                // Details
                doc.setFontSize(normalSize);

                // Invoice ID
                doc.text('Invoice ID:', leftMargin, yPos);
                doc.text(invoiceId, rightMargin, yPos, { align: 'right' });
                yPos += lineHeight * 1.5;

                // Date and Time
                doc.text('Date:', leftMargin, yPos);
                doc.text(dateTime, rightMargin, yPos, { align: 'right' });
                yPos += lineHeight * 1.5;

                // Credits
                doc.text('Credits Purchased:', leftMargin, yPos);
                doc.text(transaction.amount.toLocaleString(), rightMargin, yPos, { align: 'right' });
                yPos += lineHeight * 1.5;

                // Status
                doc.text('Status:', leftMargin, yPos);
                doc.text('Completed', rightMargin, yPos, { align: 'right' });
                yPos += lineHeight * 3;

                // Add line
                doc.setLineWidth(0.5);
                doc.line(leftMargin, yPos, rightMargin, yPos);
                yPos += lineHeight * 2;

                // Thank you message
                doc.setFontSize(subtitleSize);
                doc.text('Thank you for your purchase!', 105, yPos, { align: 'center' });
                yPos += lineHeight * 1.5;

                // Trademark message
                doc.setFontSize(smallSize);
                doc.text('Klasite is a Trademark owned by Pymify, Inc.', 105, yPos, { align: 'center' });

                // Save the PDF
                doc.save(`receipt_${invoiceId}.pdf`);
                resolve();
            };
        });
    };

    if (!transaction) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Receipt</DialogTitle>
                </DialogHeader>
                <div className="p-6 space-y-6">
                    {/* Receipt Header */}
                    <div className="text-center space-y-2">
                        <div className="flex justify-center">
                            <img
                                src={logo}
                                alt="Klasite"
                                className="h-12 w-auto"
                            />
                        </div>
                        <p className="text-muted-foreground text-sm">Credit Purchase Receipt</p>
                    </div>

                    <Separator />

                    {/* Receipt Details */}
                    <div className="space-y-4">
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Invoice ID:</span>
                            <span className="font-medium">{getInvoiceId(transaction.id)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Date:</span>
                            <span>{format(new Date(transaction.created_at), 'MMMM dd, yyyy - HH:mm')}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Credits Purchased:</span>
                            <span className="font-medium">{transaction.amount.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Status:</span>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                Completed
                            </span>
                        </div>
                    </div>

                    <Separator />

                    {/* Receipt Footer */}
                    <div className="space-y-6">
                        <p className="text-sm text-muted-foreground text-center">
                            Thank you for your purchase!
                        </p>
                        <Button
                            className="w-full flex items-center gap-2 justify-center"
                            onClick={handleDownload}
                        >
                            <Download className="h-4 w-4" />
                            Download Receipt
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default Receipt;